import { Eフェーズ種別 } from '@/openapi/enum';
import { Cデータ償却, YearValue } from '@/openapi/user';

// ------------------------------------------------------------------
// 償却用 type定義
// ------------------------------------------------------------------

/** 編集データ */
export type 償却EditItem = Cデータ償却 & {
  データpk?: number;
  番号?: number;
  フェーズ種別?: Eフェーズ種別;
};

/** 償却方法が定率法の際に使用するパラメタ */
type 定率法計算用償却率 = {
  保証率: number;
  改定償却率: number;
};

/** 償却方法が定率法の際に表示するデータ */
type 定率法償却 = 定率法計算用償却率 & {
  償却保証額: number;
  改定取得価額?: number;
};

/** 期毎の計算値 */
export type 計算減価償却 = {
  減価償却費: number;
  期末簿価: number;
  償却累計額: number;
};

/** 一覧の一行 */
export type 償却ListItem = Omit<償却EditItem, '減価償却s'> & {
  科目名: string;
  償却率?: number;
  定率法償却?: 定率法償却;
  再計算前期末簿価?: number;
  償却不足額?: number;
  部門名?: string;
  // 償却開始年から償却終了までの <年, 計算減価償却>のMap
  年次計算減価償却Map: Map<number, 計算減価償却>;
};

/** 償却不足額画面で表示する集計表（1行） */
type 科目別償却 = Pick<償却ListItem, '科目名' | '前期末簿価' | '再計算前期末簿価' | '償却不足額'>;

/** 償却不足額画面で表示する集計表（合計） */
export type 科目別償却合計 = Omit<科目別償却, '科目名'>;

/** 年度別集計表 */
export type 項目別Values = {
  項目名: string;
  values: number[];
};

/** 償却予測額画面で表示する製造原価・販管費割合別の減価償却費合計 */
export type 販管製造 = {
  販管: number;
  製造: number;
};

/** 償却予測額画面で表示する科目別の年度別期末簿価および転記・簡易表示用項目 */
export type 科目別期末簿価 = 項目別Values & {
  勘定科目Pk: number; // PLBSCF転記用
  減価償却s: number[]; // 簡易表示用
};

/** 決算期部分のヘッダ要素 */
export type 償却決算期 = {
  year: number;
  label: string;
};

/** 償却一覧 */
export type 償却List = {
  header: 償却決算期[];
  list: 償却ListItem[];
};

/** 画面用返却データ */
export type 償却 = 償却List & {
  total: {
    減価償却s: number[];
    償却不足額?: {
      償却不足額: number;
      科目別集計: {
        償却s: 科目別償却[];
        合計: 科目別償却合計;
      };
    };
    設備投資?: {
      科目別集計: {
        取得価額s: 項目別Values[];
        合計: number[];
      };
      税区分別集計: {
        課税仕入s: number[];
        課税仕入返還s: number[];
      };
    };
    償却予測額?: {
      販管製造s: 販管製造[];
      部門別減価償却s: 項目別Values[];
      科目別期末簿価s: 科目別期末簿価[];
      // PLBSCF転記用
      部門別販管製造s: {
        部門Pk: number;
        販管製造s: 販管製造[]; // size: 決算期数
      }[]; // size: 部門数
    };
  };
};

/** 年月(減価償却費自動計算時に使用) */
export type YearMonth = {
  year: number;
  month: number;
};

/** 減価償却費自動計算時の返却データ */
export type 自動計算償却 = {
  減価償却s: YearValue[];
  定率法償却?: 定率法償却;
};

/** 機能種別 */
export const 償却FunctionTypeKeys = ['償却不足額', '設備投資', '償却予測額'] as const;
export type 償却FunctionType = typeof 償却FunctionTypeKeys[number];

/** 帳票印刷 静的カラム数設定 */
export type StaticColumnConfig = {
  functionType: 償却FunctionType;
  isSimple: boolean;
  size: {
    start: number;
    end: number;
  };
};
